import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  Box,
  Check,
  CheckCheck,
  ChevronRight,
  CircleDot,
  Cloud,
  History,
  MousePointer2,
  SquarePen,
  FileText,
  GitBranch,
  GitMerge,
  Layers3,
  LayoutGrid,
  MessageCircle,
  Plus,
  RotateCcw,
  Sparkles,
  Triangle,
  Calculator,
} from "lucide-react";
import { useRef, useState, type RefObject, type ReactNode } from "react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@/components/ui/empty";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupTextarea,
} from "@/components/ui/input-group";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemTitle,
} from "@/components/ui/item";
import { Kbd } from "@/components/ui/kbd";
import { cn } from "@/lib/utils";

import { AgentCommands, type AgentCommand } from "./-agent-first-commands";
import {
  nextAgentTurn,
  postThreadReply,
  recordEvent,
  snapshotContext,
  startConversation,
} from "./-agent-first-conversation";
import {
  initialTasks,
  statusLabels,
  type Change,
  type System,
  type Task,
  type TaskStatus,
} from "./-agent-first-data";
import { Explorer } from "./-agent-first-explorer";
import {
  modelPath,
  taskFile,
  type ExplorerLocation,
} from "./-agent-first-explorer-data";
import { FileDiff } from "./-agent-first-file-diff";
import { JigIcon, JigSurface, JigWorkspace } from "./-agent-first-jigs";
import {
  jigs,
  jigSpec,
  makeJigTask,
  prepareJigTask,
  readJigInputs,
  type JigId,
  type JigInputs,
  type JigLocation,
} from "./-agent-first-jigs-data";
import { NotificationAction } from "./-agent-first-notifications";
import { runTaskPlan } from "./-agent-first-plan";
import { PlanInspector } from "./-agent-first-plan-view";
import {
  initialCanvas,
  initialMarkups,
  isTextSurface,
  type CanvasState,
  type Evidence,
} from "./-agent-first-redlines";
import { TaskReferencePanel } from "./-agent-first-reference";
import { SpatialWorkspace } from "./-agent-first-spatial";
import type { NotificationPlan } from "./-agent-first-team";

import "./-agent-first.css";

export const Route = createFileRoute("/prototypes/agent-first")({
  validateSearch: (search: Record<string, unknown>) => ({
    task: typeof search.task === "string" ? search.task : undefined,
  }),
  component: AgentFirst,
});

function SystemMark({ system }: { system: System }) {
  return (
    <span className="af-system-mark" data-system={system} aria-label={system}>
      {system === "Bear" ? <Box /> : system === "Revizto" ? "Rz" : system[0]}
    </span>
  );
}

function Status({ status }: { status: TaskStatus }) {
  return (
    <span className="af-status" data-status={status}>
      <span />
      {statusLabels[status]}
    </span>
  );
}

function AgentFirst() {
  const { task: taskId } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [tasks, setTasks] = useState(initialTasks);
  const [revision, setRevision] = useState(24);
  const [main, setMain] = useState<Record<string, string>>({});
  const [newOpen, setNewOpen] = useState(false);
  const [newPrompt, setNewPrompt] = useState("");
  const [projectView, setProjectView] = useState<
    | ({ kind: "explorer" } & ExplorerLocation)
    | ({ kind: "jig" } & JigLocation)
    | null
  >(null);
  const explorer = projectView?.kind === "explorer" ? projectView : null;
  const activeJig = projectView?.kind === "jig" ? projectView : null;
  const [jigDrafts, setJigDrafts] = useState<Record<string, JigInputs>>({});
  const [tab, setTab] = useState(
    tasks.find((task) => task.id === taskId)?.jig ? "jig" : "model",
  );
  const [referenceOpen, setReferenceOpen] = useState(
    () => window.innerWidth >= 1280,
  );
  const [canvasUI, setCanvasUI] = useState(() => initialCanvas(taskId));
  const [view, setView] = useState(taskId === "ceiling" ? "main" : "compare");
  const [reviewOpen, setReviewOpen] = useState(false);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const newAgentRef = useRef<HTMLTextAreaElement>(null);
  const textCommentRef = useRef<(() => void) | null>(null);
  const selected = tasks.find((task) => task.id === taskId);
  const attention = tasks.filter((task) =>
    ["waiting", "review", "findings"].includes(task.status),
  );
  function openTask(id?: string) {
    setProjectView(null);
    if (projectView && id && id === selected?.id) return;
    setCanvasUI(initialCanvas(id));
    setTab(tasks.find((task) => task.id === id)?.jig ? "jig" : "model");
    setView(id === "ceiling" ? "main" : "compare");
    setReviewOpen(false);
    void navigate({ search: { task: id } });
  }
  function setExplorer(location: ExplorerLocation) {
    setProjectView({ ...location, kind: "explorer" });
  }
  function openJig(id: JigId, branch = "main") {
    setProjectView({ kind: "jig", id, branch });
    setReviewOpen(false);
    if (branch === "main")
      setJigDrafts((current) => {
        const next = { ...current };
        delete next[`main/${id}`];
        return next;
      });
  }
  function openExplorer() {
    setExplorer({ branch: "main", file: modelPath, presentation: "view" });
  }
  function focusNewAgent() {
    if (!selected && !projectView) newAgentRef.current?.focus();
    else setNewOpen(true);
  }
  function openTaskExplorer(task: Task, file?: string) {
    setReviewOpen(false);
    setExplorer({
      branch: task.id,
      file:
        file ??
        (tab === "changes"
          ? (task.changes.find((change) => change.system === "Bear")?.path ??
            modelPath)
          : tab === "jig" && task.jig
            ? jigSpec(task.jig).record
            : taskFile(task, canvasUI.surface)),
      presentation: !file && tab === "changes" ? "diff" : "view",
    });
  }
  const commands: AgentCommand[] = [
    {
      id: "new",
      label: "Start an agent",
      group: "Project",
      icon: Plus,
      shortcut: "N",
      run: focusNewAgent,
      keywords: ["new", "task", "branch"],
    },
    {
      id: "workshop",
      label: "Go to workshop",
      group: "Project",
      icon: LayoutGrid,
      shortcut: "W",
      run: () => openTask(),
      keywords: ["home", "attention"],
    },
    {
      id: "explorer",
      label: "Open Explorer",
      group: "Project",
      icon: Layers3,
      shortcut: "M",
      run: openExplorer,
      keywords: ["main", "files", "branches", "project"],
    },
    ...tasks.map((task) => ({
      id: `task/${task.id}`,
      label: task.title,
      detail: `${task.trigger} · ${statusLabels[task.status]}`,
      group: "Agents" as const,
      icon: Sparkles,
      run: () => openTask(task.id),
      keywords: [task.location],
    })),
    ...jigs.map((jig) => ({
      id: `jig/${jig.id}`,
      label: `Open ${jig.name}`,
      detail: "main · Project jig",
      group: "Jigs" as const,
      icon: jig.id === "truss" ? Triangle : Calculator,
      run: () => openJig(jig.id),
      keywords: ["calculator", "jig", "canopy"],
    })),
  ];
  if (selected && !projectView) {
    commands.push({
      id: "explore-branch",
      label: "Open this branch in Explorer",
      group: "This task",
      icon: GitBranch,
      run: () => openTaskExplorer(selected),
      keywords: ["files", "source"],
    });
    commands.push({
      id: "model",
      label: "Open workspace",
      group: "This task",
      icon: Box,
      shortcut: "1",
      run: () => setTab(selected.jig ? "jig" : "model"),
    });
    if (selected.changes.length > 0)
      commands.push({
        id: "changes",
        label: "Show changes",
        group: "This task",
        icon: GitBranch,
        shortcut: "2",
        run: () => setTab("changes"),
        keywords: ["diff", "files"],
      });
    if (selected.id === "duct")
      commands.push({
        id: "compare",
        label: "Compare with main",
        group: "This task",
        icon: GitBranch,
        shortcut: "D",
        run: () => {
          setTab("model");
          setView("compare");
          setCanvasUI({ ...canvasUI, surface: "model", tool: "select" });
        },
        keywords: ["diff"],
      });
    if (selected.jig && tab === "jig") {
      const jigId = selected.jig;
      commands.push({
        id: "expand-jig",
        label: "Open this jig in full view",
        group: "This task",
        icon: ArrowUpRight,
        run: () => openJig(jigId, selected.id),
      });
    } else if (isTextSurface(selected.id, canvasUI.surface)) {
      if (tab === "model")
        commands.push({
          id: "text-comment",
          label: "Comment on selected text",
          group: "This task",
          icon: MessageCircle,
          shortcut: "C",
          run: () => textCommentRef.current?.(),
        });
    } else
      commands.push(
        {
          id: "mark-comment",
          label: "Place a comment",
          group: "This task",
          icon: MessageCircle,
          shortcut: "C",
          run: () => {
            setTab("model");
            setCanvasUI({ ...canvasUI, tool: "comment" });
          },
        },
        {
          id: "mark-cloud",
          label: "Draw a revision cloud",
          group: "This task",
          icon: Cloud,
          shortcut: "B",
          run: () => {
            setTab("model");
            setCanvasUI({ ...canvasUI, tool: "cloud" });
          },
        },
        {
          id: "mark-callout",
          label: "Add a callout",
          group: "This task",
          icon: SquarePen,
          shortcut: "A",
          run: () => {
            setTab("model");
            setCanvasUI({ ...canvasUI, tool: "callout" });
          },
        },
        {
          id: "mark-select",
          label: "Select a mark",
          group: "This task",
          icon: MousePointer2,
          shortcut: "V",
          run: () => setCanvasUI({ ...canvasUI, tool: "select" }),
        },
      );
    commands.push({
      id: "background",
      label: referenceOpen
        ? "Hide background & context"
        : "Show background & context",
      group: "This task",
      icon: History,
      shortcut: "H",
      run: () => setReferenceOpen((open) => !open),
      keywords: [
        "reasoning",
        "timeline",
        "evidence",
        "history",
        "sources",
        "sidebar",
      ],
    });
    commands.push({
      id: "reply",
      label: "Message agent",
      group: "This task",
      icon: MessageCircle,
      shortcut: "E",
      run: () => {
        composerRef.current?.focus();
      },
    });
    if (selected.status === "review")
      commands.push({
        id: "review",
        label: "Review before applying",
        group: "This task",
        icon: GitMerge,
        shortcut: "R",
        run: () => {
          prepareJigReview(selected);
          setTab("changes");
          setReviewOpen(true);
        },
      });
  }
  function updateTask(updated: Task) {
    setTasks((current) =>
      current.map((task) => (task.id === updated.id ? updated : task)),
    );
  }
  function prepareJigReview(task: Task) {
    if (!task.jig) return;
    const inputs = jigDrafts[`${task.id}/${task.jig}`];
    if (
      inputs &&
      JSON.stringify(inputs) !==
        JSON.stringify(readJigInputs(task.jig, main, task))
    )
      updateTask(prepareJigTask(task, task.jig, inputs));
  }
  function renderJig(id: JigId, task?: Task, expanded = false) {
    const key = `${task?.id ?? "main"}/${id}`;
    const saved = readJigInputs(id, main, task);
    const inputs = jigDrafts[key] ?? saved;
    const prepared =
      !!task?.changes.some((change) => change.id === `${id}-inputs`) &&
      JSON.stringify(inputs) === JSON.stringify(saved);
    return (
      <JigSurface
        key={key}
        id={id}
        task={task}
        inputs={inputs}
        baseline={readJigInputs(id, main)}
        change={(inputs) =>
          setJigDrafts((current) => ({ ...current, [key]: inputs }))
        }
        prepared={prepared}
        openSource={() =>
          setExplorer({
            branch: task?.id ?? "main",
            file: `${jigSpec(id).path}/playground.json`,
            presentation: "source",
          })
        }
        expand={expanded && task ? () => openJig(id, task.id) : undefined}
        prepare={() => {
          if (!task || task.status === "applied") {
            const created = makeJigTask(id, inputs, main, revision);
            setTasks((current) => [...current, created]);
            setProjectView(null);
            setTab("jig");
            setCanvasUI(initialCanvas(created.id));
            void navigate({ search: { task: created.id } });
          } else {
            if (!prepared) updateTask(prepareJigTask(task, id, inputs));
            openTask(task.id);
            setTab("changes");
          }
        }}
      />
    );
  }
  function applyTask(task: Task) {
    const result = runTaskPlan(task, main);
    setMain(result.state);
    setRevision(
      (current) =>
        current +
        Number(result.steps.some((step) => step.status === "applied")),
    );
    updateTask(
      recordEvent(
        {
          ...task,
          status: result.complete ? "applied" : "review",
          summary: result.complete
            ? "Changes applied to the main project and mock connected tools."
            : "Execution stopped at changed state. Inspect the run before retrying.",
          planRuns: [
            ...task.planRuns,
            {
              kind: "apply",
              steps: result.steps,
              complete: result.complete,
              notifications: result.notifications,
            },
          ],
        },
        result.complete
          ? task.notifications
            ? `Applied the reviewed changes to the mock project and connected tools. Simulated notifications to ${task.notifications.recipients.map((recipient) => `${recipient.name} via ${recipient.channel}`).join(", ")}. The run is saved with this task.`
            : "Applied the reviewed changes to the mock project and connected tools. The run is saved with this task."
          : "The mock apply stopped at changed state. Completed steps are retained in the run record.",
      ),
    );
  }
  function createTask(brief: string) {
    const firstLine = brief.split("\n")[0];
    const title =
      firstLine.length > 80
        ? `${firstLine.slice(0, 77).trimEnd()}…`
        : firstLine;
    const id = crypto.randomUUID();
    const task: Task = {
      id,
      title,
      trigger: "Your query",
      purpose: "change",
      evidence: [],
      status: "working",
      summary: "Reading the brief and project context.",
      location: "Project-wide",
      branch: `task/${id.slice(0, 8)}`,
      base: revision,
      baseState: { ...main },
      changes: [],
      markups: initialMarkups(id),
      conversation: startConversation(brief),
      planRuns: [],
      notifications: null,
      context: [{ title: "Task brief", source: "You", body: brief }],
    };
    setTasks((current) => [...current, task]);
    setNewOpen(false);
    setNewPrompt("");
    openTask(id);
  }
  function reset() {
    setTasks(initialTasks());
    setMain({});
    setRevision(24);
    setNewPrompt("");
    setJigDrafts({});
    openTask();
  }
  return (
    <div className="af-theme af-app">
      <aside className="af-sidebar">
        <Link to="/prototypes" className="af-brand">
          <span className="af-brand-icon" aria-hidden="true">
            <Box />
          </span>
          Bear<span className="af-prototype-label">PROTOTYPE</span>
        </Link>
        <div className="af-project">
          <span className="af-project-monogram">N</span>
          <div>
            <strong>Northline Medical</strong>
            <span>Design coordination</span>
          </div>
        </div>
        <Button className="af-start-button" onClick={focusNewAgent}>
          <Plus data-icon="inline-start" />
          Start an agent
          <Kbd aria-hidden="true">N</Kbd>
        </Button>
        <nav className="af-nav" aria-label="Project navigation">
          <button
            className={cn(
              "af-nav-link",
              !selected && !projectView && "af-nav-active",
            )}
            onClick={() => openTask()}
          >
            <LayoutGrid />
            Workshop<span className="af-nav-count">{attention.length}</span>
          </button>
          <button
            className={cn("af-nav-link", explorer && "af-nav-active")}
            onClick={openExplorer}
          >
            <Layers3 />
            Explorer
          </button>
        </nav>
        <div className="af-sidebar-label">
          JIGS<span>{jigs.length}</span>
        </div>
        <nav className="af-jig-nav" aria-label="Project jigs">
          {jigs.map((jig) => (
            <button
              key={jig.id}
              className={cn(
                "af-nav-link",
                activeJig?.id === jig.id && "af-nav-active",
              )}
              onClick={() => openJig(jig.id)}
            >
              <JigIcon id={jig.id} />
              {jig.name}
            </button>
          ))}
        </nav>
        <div className="af-sidebar-label">
          AGENT TASKS
          <span>{tasks.length}</span>
        </div>
        <nav className="af-task-nav" aria-label="Agent tasks">
          {tasks.map((task) => (
            <button
              key={task.id}
              className={cn(
                "af-task-link",
                selected?.id === task.id && !projectView && "af-task-active",
              )}
              onClick={() => openTask(task.id)}
            >
              <span className="af-task-dot" data-status={task.status} />
              <span>{task.title}</span>
            </button>
          ))}
          <Collapsible>
            <CollapsibleTrigger className="af-task-link af-completed-tasks">
              <ChevronRight aria-hidden="true" />
              <span>Completed tasks</span>
            </CollapsibleTrigger>
            <CollapsibleContent />
          </Collapsible>
        </nav>
        <div className="af-sidebar-bottom">
          <span className="af-avatar">AL</span>
          <div>
            <strong>Alex Lee</strong>
            <span>Project team</span>
          </div>
          <Badge variant="outline">Demo</Badge>
        </div>
      </aside>
      <main className="af-main">
        <header className="af-topbar">
          <nav className="af-breadcrumb" aria-label="Breadcrumb">
            <ol>
              <li>Northline Medical Center</li>
              <li aria-hidden="true">
                <ChevronRight />
              </li>
              {selected && !projectView ? (
                <>
                  <li>
                    <button onClick={() => openTask()}>Workshop</button>
                  </li>
                  <li aria-hidden="true">
                    <ChevronRight />
                  </li>
                  <li
                    aria-current="page"
                    className="af-breadcrumb-current"
                    title={selected.title}
                  >
                    {selected.title}
                  </li>
                </>
              ) : (
                <li aria-current="page" className="af-breadcrumb-current">
                  {activeJig
                    ? jigSpec(activeJig.id).name
                    : explorer
                      ? "Explorer"
                      : "Workshop"}
                </li>
              )}
            </ol>
          </nav>
          <AgentCommands commands={commands} blocked={newOpen || reviewOpen} />
        </header>
        {selected && (
          <div className="af-task-view" hidden={!!projectView}>
            <TaskWorkspace
              key={selected.id}
              task={selected}
              revision={revision}
              main={main}
              update={updateTask}
              apply={applyTask}
              prepareReview={() => prepareJigReview(selected)}
              back={() => openTask()}
              openExplorer={(file) => openTaskExplorer(selected, file)}
              tab={tab}
              setTab={setTab}
              view={view}
              setView={setView}
              reviewOpen={reviewOpen}
              setReviewOpen={setReviewOpen}
              composerRef={composerRef}
              textCommentRef={textCommentRef}
              canvasUI={canvasUI}
              setCanvasUI={setCanvasUI}
              jigSurface={
                selected.jig
                  ? renderJig(selected.jig, selected, true)
                  : undefined
              }
              referenceOpen={referenceOpen && !projectView}
              setReferenceOpen={setReferenceOpen}
            />
          </div>
        )}
        {activeJig ? (
          <JigWorkspace
            location={activeJig}
            tasks={tasks}
            revision={revision}
            setLocation={(location) =>
              setProjectView({ ...location, kind: "jig" })
            }
            openTask={openTask}
          >
            {renderJig(
              activeJig.id,
              tasks.find((task) => task.id === activeJig.branch),
            )}
          </JigWorkspace>
        ) : explorer ? (
          <Explorer
            tasks={tasks}
            main={main}
            revision={revision}
            location={explorer}
            setLocation={setExplorer}
            origin={selected}
            openTask={openTask}
          />
        ) : (
          !selected && (
            <Workshop
              tasks={tasks}
              revision={revision}
              openTask={openTask}
              createTask={createTask}
              prompt={newPrompt}
              setPrompt={setNewPrompt}
              promptRef={newAgentRef}
            />
          )
        )}
        <footer className="af-demo-footer">
          <span>
            Interactive prototype · Scripted agents & simulated tools · Resets
            on refresh
          </span>
          <Button variant="ghost" size="sm" onClick={reset}>
            <RotateCcw data-icon="inline-start" />
            Reset demo
          </Button>
        </footer>
      </main>
      <NewTaskDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        revision={revision}
        create={createTask}
        prompt={newPrompt}
        setPrompt={setNewPrompt}
      />
    </div>
  );
}

function Workshop({
  tasks,
  revision,
  openTask,
  createTask,
  prompt,
  setPrompt,
  promptRef,
}: {
  tasks: Task[];
  revision: number;
  openTask: (id: string) => void;
  createTask: (prompt: string) => void;
  prompt: string;
  setPrompt: (prompt: string) => void;
  promptRef: RefObject<HTMLTextAreaElement | null>;
}) {
  const attention = tasks.filter((task) =>
    ["waiting", "review", "findings"].includes(task.status),
  );
  const working = tasks.filter((task) => task.status === "working");
  const applied = tasks.filter((task) => task.status === "applied");
  return (
    <div className="af-overview">
      <section
        className="af-overview-start"
        aria-labelledby="start-agent-heading"
      >
        <h1 id="start-agent-heading">What should we work on?</h1>
        <AgentPrompt
          revision={revision}
          prompt={prompt}
          setPrompt={setPrompt}
          create={createTask}
          inputRef={promptRef}
        />
      </section>
      <section aria-labelledby="attention-heading">
        <div className="af-section-heading">
          <h2 id="attention-heading">
            <CircleDot />
            Needs your attention<span>{attention.length}</span>
          </h2>
        </div>
        {attention.length ? (
          <ItemGroup
            className="af-attention-list"
            aria-labelledby="attention-heading"
          >
            {attention.map((task) => (
              <div key={task.id} role="listitem">
                <Item asChild size="sm" className="af-attention-row">
                  <button onClick={() => openTask(task.id)}>
                    <Status status={task.status} />
                    <ItemContent className="af-attention-copy">
                      <ItemTitle>{task.title}</ItemTitle>
                      <ItemDescription className="af-attention-summary">
                        {task.summary}
                      </ItemDescription>
                    </ItemContent>
                    <div className="af-attention-context">
                      <span>{task.trigger}</span>
                      <small>{task.location}</small>
                    </div>
                    <ItemActions className="af-attention-count">
                      <span>
                        {task.purpose === "investigation"
                          ? task.evidence.length === 1
                            ? "1 source"
                            : `${task.evidence.length} sources`
                          : task.changes.length === 1
                            ? "1 change"
                            : `${task.changes.length} changes`}
                      </span>
                      <ChevronRight />
                    </ItemActions>
                  </button>
                </Item>
              </div>
            ))}
          </ItemGroup>
        ) : (
          <Empty className="af-caught-up">
            <EmptyHeader>
              <CheckCheck />
              <EmptyTitle>Nothing waiting on you</EmptyTitle>
              <EmptyDescription>
                Decisions and completed work will appear here.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        )}
      </section>
      <section aria-labelledby="working-heading" className="af-working-section">
        <div className="af-section-heading">
          <h2 id="working-heading">
            <Sparkles />
            Working in the background<span>{working.length}</span>
          </h2>
        </div>
        <div className="af-working-list">
          {working.map((task) => (
            <button
              key={task.id}
              className="af-working-row"
              onClick={() => openTask(task.id)}
            >
              <span className="af-agent-icon">
                <Sparkles />
              </span>
              <div>
                <h3>{task.title}</h3>
                <p>{task.summary}</p>
              </div>
              <span className="af-row-trigger">{task.trigger}</span>
              <Status status={task.status} />
              <ArrowUpRight />
            </button>
          ))}
          {!working.length && (
            <p className="af-empty-working">
              No agents running. Start a task when you're ready.
            </p>
          )}
        </div>
      </section>
      {applied.length > 0 && (
        <section className="af-applied-list">
          <div className="af-eyebrow">APPLIED THIS SESSION</div>
          {applied.map((task) => (
            <button key={task.id} onClick={() => openTask(task.id)}>
              <Check />
              <span>{task.title}</span>
              <span>
                {task.changes.length === 1
                  ? "1 change applied"
                  : `${task.changes.length} changes applied`}
              </span>
              <ChevronRight />
            </button>
          ))}
        </section>
      )}
    </div>
  );
}

function TaskWorkspace({
  task,
  revision,
  main,
  update,
  apply,
  prepareReview,
  back,
  openExplorer,
  tab,
  setTab,
  view,
  setView,
  reviewOpen,
  setReviewOpen,
  composerRef,
  textCommentRef,
  canvasUI,
  setCanvasUI,
  referenceOpen,
  setReferenceOpen,
  jigSurface,
}: {
  task: Task;
  revision: number;
  main: Record<string, string>;
  update: (task: Task) => void;
  apply: (task: Task) => void;
  prepareReview: () => void;
  back: () => void;
  openExplorer: (file?: string) => void;
  tab: string;
  setTab: (tab: string) => void;
  view: string;
  setView: (view: string) => void;
  reviewOpen: boolean;
  setReviewOpen: (open: boolean) => void;
  composerRef: RefObject<HTMLTextAreaElement | null>;
  textCommentRef: RefObject<(() => void) | null>;
  canvasUI: CanvasState;
  setCanvasUI: (ui: CanvasState) => void;
  referenceOpen: boolean;
  setReferenceOpen: (open: boolean) => void;
  jigSurface?: ReactNode;
}) {
  const isApplied = task.status === "applied";
  function openSource(source: Evidence) {
    setTab("model");
    setCanvasUI({
      ...canvasUI,
      source,
      surface: source.surface,
      selected: source.mark,
      tool: "select",
    });
    setView(source.view);
  }
  function decide() {
    const mark = task.markups.find((mark) => mark.id === "proposal");
    if (!mark) return;
    const updated = postThreadReply(
      task,
      "Use the east route. Keep the existing ceiling requirement and leave fabrication detail open.",
      snapshotContext(mark, task.base, "compare"),
      true,
      "East route confirmed. The five coordinated changes are ready for your review. The ceiling requirement is retained, and fabrication detail remains open. Nothing has been applied yet.",
    );
    update({
      ...updated,
      status: "review",
      summary: "East route confirmed. Five coordinated changes ready to apply.",
    });
  }
  function prepareExample() {
    update({
      ...task,
      status: "review",
      summary: "An example task note is ready for review.",
      conversation: [
        ...task.conversation,
        nextAgentTurn(
          task,
          "I prepared an illustrative task note for review. No technical checks have run, and no changes have been applied.",
          [],
        ),
      ],
      changes: [
        {
          id: `note-${task.id}`,
          system: "Bear",
          title: "Add task review note",
          path: `notes/${task.branch}.md`,
          language: "markdown",
          before: "",
          after: `# ${task.title}\n\n## Review note\nThis is an illustrative result; no technical checks have run.\n`,
        },
      ],
    });
    setTab("changes");
  }
  return (
    <div className="af-workspace">
      <div className="af-task-heading">
        <Button
          variant="ghost"
          size="icon"
          aria-label="Back to workshop"
          onClick={back}
        >
          <ArrowLeft />
        </Button>
        <div className="af-task-heading-copy">
          <h1>{task.title}</h1>
          <p>
            {task.location}
            <span>·</span>
            <Status status={task.status} />
            <span className="af-task-branch">
              <span className="af-branch-name">
                <GitBranch aria-hidden="true" />
                {task.branch}
              </span>
              <span>from main r{task.base}</span>
              {isApplied && <span>Included in main · r{revision}</span>}
            </span>
          </p>
        </div>
        <div className="af-task-actions">
          <Button variant="ghost" onClick={() => openExplorer()}>
            Open in Explorer
            <ArrowUpRight data-icon="inline-end" />
          </Button>
          {task.changes.length > 0 && (
            <Button
              onClick={() => {
                prepareReview();
                setTab("changes");
                setReviewOpen(true);
              }}
              disabled={task.status !== "review"}
            >
              <GitMerge data-icon="inline-start" />
              {isApplied ? "Changes applied" : "Review & apply"}
            </Button>
          )}
        </div>
      </div>
      <div className="af-task-body">
        <div className="af-workspace-grid af-canvas-workspace">
          <div className="af-artifacts">
            <SpatialWorkspace
              task={task}
              active={tab === "model"}
              update={update}
              ui={canvasUI}
              setUI={setCanvasUI}
              view={view}
              setView={setView}
              composerRef={composerRef}
              textCommentRef={textCommentRef}
              decide={decide}
              prepare={prepareExample}
              openWorkspace={() => setTab("model")}
              openChanges={() => setTab("changes")}
              extension={
                task.jig
                  ? {
                      label: jigSpec(task.jig).name,
                      active: tab === "jig",
                      open: () => setTab("jig"),
                      content: jigSurface,
                    }
                  : undefined
              }
            >
              <div className="af-content-heading">
                <div className="af-eyebrow">
                  {isApplied ? "TASK RECORD" : "THIS BRANCH → MAIN"}
                </div>
                <h2>
                  {isApplied
                    ? "The changes you applied"
                    : "One task. All its changes."}
                </h2>
                <p>
                  {isApplied
                    ? "Each destination below was updated in the demo."
                    : "Review the model, files, and external actions as one set."}
                </p>
                {revision > task.base && !isApplied && (
                  <p className="af-revision-note">
                    Main is now r{revision}. This task started from r{task.base}
                    .
                  </p>
                )}
              </div>
              {task.changes.length ? (
                <ChangeList
                  changes={task.changes}
                  notifications={task.notifications}
                  openContacts={openExplorer}
                  main={main}
                  applied={isApplied}
                />
              ) : (
                <Empty>
                  <EmptyHeader>
                    <GitBranch />
                    <EmptyTitle>No proposed changes yet</EmptyTitle>
                    <EmptyDescription>
                      This agent is still working. Its model edits, files, and
                      tool actions will collect here.
                    </EmptyDescription>
                  </EmptyHeader>
                </Empty>
              )}
              <PlanInspector task={task} main={main} update={update} />
            </SpatialWorkspace>
          </div>
        </div>
        <TaskReferencePanel
          task={task}
          open={referenceOpen}
          onOpenChange={setReferenceOpen}
          openSource={openSource}
        >
          <section className="af-context-panel" aria-label="Task context">
            <div className="af-content-heading">
              <h2>Context</h2>
            </div>
            {task.context.map((context) => (
              <details className="af-context-item" key={context.title}>
                <summary>
                  <FileText />
                  <span>
                    <strong>{context.title}</strong>
                    <small>{context.source}</small>
                  </span>
                  <ChevronRight />
                </summary>
                <p>{context.body}</p>
              </details>
            ))}
          </section>
        </TaskReferencePanel>
      </div>
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="af-theme af-review-dialog">
          <DialogHeader>
            <DialogTitle>Apply this task's changes</DialogTitle>
            <DialogDescription>
              {task.title}. Review the complete set before updating main and the
              connected tools.
            </DialogDescription>
          </DialogHeader>
          <div className="af-review-scroll">
            <ChangeList
              changes={task.changes}
              notifications={task.notifications}
              openContacts={openExplorer}
              main={main}
              applied={false}
              expanded
            />
            <Alert>
              <GitMerge />
              <AlertTitle>Apply in sequence</AlertTitle>
              <AlertDescription>
                Each destination is checked before its step runs. Completed
                steps are retained if a later step stops; retrying skips them.
                The demo records each result against mock destinations.
              </AlertDescription>
            </Alert>
          </div>
          <div className="af-review-footer">
            <span>
              {task.notifications
                ? `${task.changes.length} changes · ${task.notifications.recipients.length} notifications · Mock actions only`
                : task.changes.length === 1
                  ? "1 change · Mock actions only"
                  : `${task.changes.length} changes · Mock actions only`}
            </span>
            <Button
              onClick={() => {
                apply(task);
                setReviewOpen(false);
              }}
            >
              Apply plan
              <ArrowRight data-icon="inline-end" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ChangeList({
  changes,
  notifications,
  openContacts,
  main,
  applied,
  expanded = false,
}: {
  changes: Change[];
  notifications: NotificationPlan | null;
  openContacts: (path: string) => void;
  main: Record<string, string>;
  applied: boolean;
  expanded?: boolean;
}) {
  return (
    <div className="af-change-list">
      {changes.map((change) => (
        <details
          key={change.id}
          className="af-change-item"
          open={expanded || undefined}
        >
          <summary>
            <SystemMark system={change.system} />
            <span>
              <strong>{change.title}</strong>
              <small>
                {change.system === "Bear" ? change.path : change.detail}
              </small>
              {change.jig && (
                <span className="af-jig-origin">
                  From {jigSpec(change.jig).name}
                </span>
              )}
            </span>
            <Badge variant="outline">
              {applied
                ? "Applied"
                : change.system === "Bear"
                  ? "Update"
                  : "Prepared action"}
            </Badge>
            <ChevronRight />
          </summary>
          {change.system === "Bear" ? (
            <FileDiff
              path={change.path}
              language={change.language}
              before={
                applied ? change.before : (main[change.id] ?? change.before)
              }
              after={change.after}
              applied={applied}
            />
          ) : (
            <div className="af-diff">
              <div>
                <span>
                  {applied ? "Before this task" : "Main / current tool state"}
                </span>
                <p>
                  {applied ? change.before : (main[change.id] ?? change.before)}
                </p>
              </div>
              <div>
                <span>
                  {applied ? "Applied result" : "This task / proposed action"}
                </span>
                <p>{change.after}</p>
              </div>
            </div>
          )}
        </details>
      ))}
      {notifications && (
        <NotificationAction
          plan={notifications}
          applied={applied}
          openContacts={openContacts}
          expanded={expanded}
        />
      )}
    </div>
  );
}

function NewTaskDialog({
  open,
  onOpenChange,
  revision,
  create,
  prompt,
  setPrompt,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  revision: number;
  create: (prompt: string) => void;
  prompt: string;
  setPrompt: (prompt: string) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="af-theme af-new-dialog">
        <DialogHeader>
          <DialogTitle>What should we work on?</DialogTitle>
          <DialogDescription>
            An agent gets its own branch from main r{revision}, with the model,
            tools, and project context.
          </DialogDescription>
        </DialogHeader>
        <AgentPrompt
          revision={revision}
          prompt={prompt}
          setPrompt={setPrompt}
          create={create}
          autoFocus
        />
      </DialogContent>
    </Dialog>
  );
}

function AgentPrompt({
  revision,
  prompt,
  setPrompt,
  create,
  inputRef,
  autoFocus,
}: {
  revision: number;
  prompt: string;
  setPrompt: (prompt: string) => void;
  create: (prompt: string) => void;
  inputRef?: RefObject<HTMLTextAreaElement | null>;
  autoFocus?: boolean;
}) {
  const [modifier] = useState(() =>
    /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘" : "Ctrl",
  );
  return (
    <form
      className="af-agent-prompt"
      aria-label="Start a new agent"
      onSubmit={(event) => {
        event.preventDefault();
        if (prompt.trim()) create(prompt.trim());
      }}
    >
      <InputGroup>
        <InputGroupTextarea
          ref={inputRef}
          autoFocus={autoFocus}
          aria-label="Describe the task for a new agent"
          placeholder="Describe a task, ask a question, or investigate a change…"
          value={prompt}
          onChange={(event) => setPrompt(event.target.value)}
          rows={3}
          required
          onKeyDown={(event) => {
            if (
              (event.metaKey || event.ctrlKey) &&
              event.key === "Enter" &&
              !event.nativeEvent.isComposing &&
              !event.repeat
            ) {
              event.preventDefault();
              event.currentTarget.form?.requestSubmit();
            }
          }}
        />
        <InputGroupAddon align="block-end" className="af-agent-prompt-footer">
          <span className="af-prompt-branch">
            <GitBranch />
            From main · r{revision}
          </span>
          <Kbd aria-hidden="true">{modifier} ↵</Kbd>
          <Button
            type="submit"
            disabled={!prompt.trim()}
            aria-keyshortcuts="Meta+Enter Control+Enter"
          >
            Start agent <ArrowUpRight data-icon="inline-end" />
          </Button>
        </InputGroupAddon>
      </InputGroup>
    </form>
  );
}
